import {
  Box,
  Archive,
  Calculator,
  Calendar,
  ChevronDownIcon,
  CreditCard,
  DollarSign,
  Package,
  Settings,
  HelpCircle,
  Shield,
  Truck,
  Users,
  MessageSquare,
  Rocket,
  Globe,
  Building2,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { useSidebar } from "~/providers/sidebar.provider";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import { useFeedbackStore } from "~/store/feedback.store"
import { FeedbackModal } from "~/components/common/Feedback";


 // Add this import

type NavItem = {
  name?: string;
  icon?: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
  gap?: boolean;
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const { user } = useAuth();
  const location = useLocation();
  const { openModal } = useFeedbackStore(); // Add this hook

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Create navItems based on user role
  const navItems = useMemo(
    () => [
      {
        name: "Bookings",
        icon: <Calendar size={20} />,
        path: "/bookings",
      },
      {
        name: "Clients",
        icon: <Users size={20} />,
        path: "/client/list",
      },
      {
        name: "Inventory",
        icon: <Package size={20} />,
        path: "/client/inventory",
      },
      {
        name: "AHC Storage",
        icon: <Archive size={20} />,
        path: "/ahc-storage",
      },
      { gap: true }, // Gap after Inventory.
      {
        name: "Dispatch Tools",
        icon: <Truck size={20} />,
        subItems: [
          { name: "Summaries", path: "/dispatch/summaries" },
          {
            name: "Load Lists",
            path: "/dispatch/load-lists",
          },
          {
            name: "Quick Add Staff/Inventory",
            path: "/dispatch/quick-add",
          },
          {
            name: "Inventory Control",
            path: "/dispatch/inventory-control",
          },
          {
            name: "Internal Events/Tasks",
            path: "/dispatch/internal-events",
          },
          {
            name: "Print Labels",
            path: "/dispatch/print-labels",
          },
        ],
      },
      {
        name: "Mission Control",
        icon: <Rocket size={20} />,
        path: "/operations/mission-control",
      },
      { gap: true }, // Gap after Mission Control
      {
        name: "Operations",
        icon: <Settings size={20} />,
        subItems: [
          { name: "Staffing", path: "/operations/staffing" },
          {
            name: "Vessels + Shuttles",
            path: "/operations/vessels-and-shuttles",
          },
          {
            name: "Supply and Materials",
            path: "/operations/supplies",
          },
          {
            name: "Team + Client Notifications",
            path: "/notifications",
          },
          ...(user?.role === Role.SUPER_ADMIN
            ? [{ name: "Backend Setup", path: "/operations/backend-setup" }]
            : []),
          ...(user?.role === Role.SUPER_ADMIN
            ? [{ name: "Service Metrics", path: "/operations/service-metrics" }]
            : []),
        ],
      },
      {
        name: "QB Connect (Pending)",
        icon: <DollarSign size={20} />,
        subItems: [
          { name: "Dashboard", path: "/billing/dashboard" },
          { name: "Invoicing", path: "/billing/invoicing" },
          { name: "Storage Invoicing", path: "/billing/storage" },
          { name: "Sales", path: "/billing/sales" },
          {
            name: "Expenses Management",
            path: "/billing/expenses",
          },
          { name: "Payroll", path: "/billing/payroll" },
        ],
      },
      {
        name: "Ask Oz",
        icon: <Sparkles size={20} />,
        path: "/ask-oz",
      },
      {
        name: "Help & Support",
        icon: <HelpCircle size={20} />,
        path: "/help-resources",
      },
      { gap: true },
      {
        name: "Network",
        icon: <Globe size={20} />,
        path: "/network",
      },
      {
        name: "Organization",
        icon: <Building2 size={20} />,
        path: "/organization-settings",
      },
    ],
    []
  );

  // Admin items with spacing
  const adminItems = useMemo(
    () => [
      ...(user?.role === Role.SUPER_ADMIN
        ? [
            {
              name: "Admin Management",
              icon: <Shield size={20} />,
              path: "/admin-management",
            },
            {
              name: "SaaS Control",
              icon: <CreditCard size={20} />,
              path: "/saas-control",
            },
          ]
        : []),
    ],
    [user?.role]
  );

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    navItems.forEach((nav, index) => {
      if (nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "main",
              index,
            });
            submenuMatched = true;
          }
        });
      }
    });

    // Check admin items too (if they have subItems)
    adminItems.forEach((nav, index) => {
      if ('subItems' in nav && nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "others",
              index,
            });
            submenuMatched = true;
          }
        });
      }
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, navItems, adminItems]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-1">
      {items.map((nav, index) => {
        // Handle divider items
        if ('gap' in nav && nav.gap) {
          return (
            <li key={`divider-${index}`} className="flex justify-center py-4 text-xs text-gray-400">
              ---
            </li>
          );
        }

        return (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`flex items-center w-full gap-2 px-2 py-2 font-medium text-sm transition-all ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "bg-black text-white"
                  : "bg-white text-black hover:bg-gray-50"
              } ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span className="flex-shrink-0 text-xs">
                {openSubmenu?.type === menuType && openSubmenu?.index === index ? ">" : " "}
              </span>
              <span className="flex-shrink-0">
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="flex-1 text-left">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200 flex-shrink-0 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`flex items-center w-full gap-2 px-2 py-2 font-medium text-sm transition-all ${
                  isActive(nav.path) 
                    ? "bg-black text-white" 
                    : "bg-white text-black hover:bg-gray-50"
                } ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "lg:justify-start"
                }`}
              >
                <span className="flex-shrink-0 text-xs">
                  {isActive(nav.path) ? ">" : " "}
                </span>
                <span className="flex-shrink-0">
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span>{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-1 space-y-0.5 ml-6">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`flex items-center gap-2 px-2 py-1.5 font-medium text-xs ${
                        isActive(subItem.path)
                          ? "bg-black text-white"
                          : "bg-white text-black hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex-shrink-0 text-xs">
                        {isActive(subItem.path) ? ">" : "|"}
                      </span>
                      <span className="flex-1">{subItem.name}</span>
                      <span className="flex items-center gap-1 ml-auto">
                        {subItem.new && (
                          <span
                            className={`px-1.5 py-0.5 text-xs font-semibold ${
                              isActive(subItem.path)
                                ? "bg-white text-black"
                                : "bg-black text-white"
                            }`}
                          >
                            [new]
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`px-1.5 py-0.5 text-xs font-semibold ${
                              isActive(subItem.path)
                                ? "bg-white text-black"
                                : "bg-black text-white"
                            }`}
                          >
                            [pro]
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
        );
      })}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-3 left-0 bg-white text-black h-screen transition-all duration-300 ease-in-out z-[60]
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderRight: '2px solid black',
        boxShadow: 'none'
      }}
    >
      <div
        className={`py-4 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <div className="flex items-center gap-2">
              <img className="size-6" src="/logo.png" alt="Logo" />
              <span className="ml-2 text-sm font-semibold">
                 {user?.organization?.name || "Art Handler Collective"}
              </span>
            </div>
          ) : (
            <div className="flex justify-center w-full">
              <img className="size-6" src="/logo.png" alt="Logo" />
            </div>
          )}
        </Link>
      </div>
      
      <div className="flex flex-col flex-1 overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-4 pt-8">
          <div className="flex flex-col">
            <div>{renderMenuItems(navItems, "main")}</div>
            {adminItems.length > 0 && (
              <>
                <div className="my-4" /> {/* Spacing above admin section */}
                <div>{renderMenuItems(adminItems, "others")}</div>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Add Feedback Button at Bottom - Only for Super Admins */}
      {(user?.role === Role.SUPER_ADMIN || user?.role === Role.ADMIN) && (
        <div className="mt-auto pb-4 pt-3">
          <div className="text-xs text-gray-400 mb-2">---</div>
          <button
            onClick={openModal}
            className={`w-full flex items-center gap-2 px-2 py-2 font-medium text-sm bg-white text-black hover:bg-gray-50 transition-colors ${
              !isExpanded && !isHovered && !isMobileOpen
                ? "justify-center"
                : "justify-start"
            }`}
          >
            <MessageSquare size={18} />
            {(isExpanded || isHovered || isMobileOpen) && (
              <span>App Feedback</span>
            )}
          </button>
        </div>
      )}

      {/* Add Feedback Modal */}
      <FeedbackModal />
    </aside>
  );
};

export default AppSidebar;